const clamp = (value: number, min = 0, max = 255): number =>
  Math.min(max, Math.max(min, value));

export const parseHexColor = (hex: string): { r: number; g: number; b: number } | null => {
  const normalized = hex.trim().replace(/^#/, '');
  if (!/^[0-9A-Fa-f]{6}$/.test(normalized)) return null;
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
};

export const toHexColor = (r: number, g: number, b: number): string =>
  `#${[r, g, b]
    .map((channel) => clamp(Math.round(channel)).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;

export const withAlpha = (hex: string, alpha: number): string => {
  const rgb = parseHexColor(hex);
  if (!rgb) return hex;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

export const darkenHex = (hex: string, amount = 0.12): string => {
  const rgb = parseHexColor(hex);
  if (!rgb) return hex;
  const factor = 1 - amount;
  return toHexColor(rgb.r * factor, rgb.g * factor, rgb.b * factor);
};

const relativeLuminance = (hex: string): number => {
  const rgb = parseHexColor(hex);
  if (!rgb) return 0;
  const channels = [rgb.r, rgb.g, rgb.b].map((value) => {
    const channel = value / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
};

/** Pick readable text color on top of a filled primary button. */
export const getOnPrimaryColor = (primaryHex: string, shellHex: string): string => {
  const primaryLum = relativeLuminance(primaryHex);
  const shellLum = relativeLuminance(shellHex);
  const whiteContrast = (1 + 0.05) / (primaryLum + 0.05);
  const darkContrast = (primaryLum + 0.05) / (shellLum + 0.05);
  return whiteContrast >= darkContrast ? '#FFFFFF' : shellHex;
};

export interface BrandCssVars {
  '--brand-primary': string;
  '--brand-primary-hover': string;
  '--brand-primary-05': string;
  '--brand-primary-10': string;
  '--brand-primary-12': string;
  '--brand-primary-15': string;
  '--brand-primary-19': string;
  '--brand-primary-25': string;
  '--brand-primary-30': string;
  '--brand-primary-35': string;
  '--brand-primary-50': string;
  '--brand-primary-55': string;
  '--brand-primary-75': string;
  '--brand-shell-bg': string;
  '--brand-on-primary': string;
}

export const buildBrandCssVars = (
  primaryColor: string,
  shellBackgroundColor: string
): BrandCssVars => ({
  '--brand-primary': primaryColor,
  '--brand-primary-hover': darkenHex(primaryColor, 0.12),
  '--brand-primary-05': withAlpha(primaryColor, 0.05),
  '--brand-primary-10': withAlpha(primaryColor, 0.1),
  '--brand-primary-12': withAlpha(primaryColor, 0.125),
  '--brand-primary-15': withAlpha(primaryColor, 0.15),
  '--brand-primary-19': withAlpha(primaryColor, 0.188),
  '--brand-primary-25': withAlpha(primaryColor, 0.25),
  '--brand-primary-30': withAlpha(primaryColor, 0.3),
  '--brand-primary-35': withAlpha(primaryColor, 0.35),
  '--brand-primary-50': withAlpha(primaryColor, 0.5),
  '--brand-primary-55': withAlpha(primaryColor, 0.55),
  '--brand-primary-75': withAlpha(primaryColor, 0.75),
  '--brand-shell-bg': shellBackgroundColor,
  '--brand-on-primary': getOnPrimaryColor(primaryColor, shellBackgroundColor),
});
